// A snap scroller as a gesture: the drag and its momentum run on the compositor with the browser's own
// fling physics, so this only reports position back. The settle DETECTION is the subtle part: a held
// finger never settles, and a mandatory-snap scroller may not rest between slots.

/** `0` at rest → `1` fully away; axis-agnostic (`scrollTop` for a sheet, `scrollLeft` for the menu). */
export function snapProgress(scrolled: number, maxScroll: number): number {
	if (maxScroll <= 0) return 0;
	const progress = 1 - scrolled / maxScroll;
	return progress < 0 ? 0 : progress > 1 ? 1 : progress;
}

/**
 * Mandatory snap only rests ON a slot, so this is not the user's decision (the browser's fling made it):
 * it absorbs a momentum tail still creeping when the settle fires.
 */
export const SNAP_LANDED = 0.98;

export function settledDismissed(progress: number): boolean {
	return progress >= SNAP_LANDED;
}

/** Where `scrollend` is missing. */
export const SNAP_SETTLE_DEBOUNCE_MS = 140;

export interface SnapScrollerHandlers {
	onStart?: () => void;
	/** Every rAF while it moves: reads plus cheap writes only. */
	onFrame: () => void;
	/** `wasScrolling` is false for a settle nobody owned. */
	onSettle: (wasScrolling: boolean) => void;
}

/** Coalesced to one `onFrame` per frame: a starved frame lags a derived cue, never the gesture itself. */
export function observeSnapScroller(el: HTMLElement, handlers: SnapScrollerHandlers): { stop: () => void } {
	let frame = 0;
	let settleTimer = 0;
	let scrolling = false;
	/**
	 * While a finger is down nothing settles: the debounce fires 140ms after the last scroll event, and
	 * `scrollend` can arrive with the touch still down once the finger leaves the box. TOUCH events, since
	 * `pointercancel` ends the instant a native scroll claims the gesture; on `window`, for outside releases.
	 */
	let held = false;
	/** Owed to the release, which re-arms the debounce. */
	let owed = false;
	/**
	 * A settle under a held finger means the browser is about to animate to the nearest slot under the
	 * finger, which yanks it and oscillates. Restoring snap on release is itself the snap-back.
	 */
	let snapSuppressed = false;

	const suppressSnap = () => {
		if (snapSuppressed) return;
		snapSuppressed = true;
		el.style.scrollSnapType = 'none';
	};

	const restoreSnap = () => {
		if (!snapSuppressed) return;
		snapSuppressed = false;
		el.style.scrollSnapType = '';
	};

	const supportsScrollEnd = 'onscrollend' in window;

	const publish = () => {
		frame = 0;
		handlers.onFrame();
	};

	const armDebounce = () => {
		clearTimeout(settleTimer);
		settleTimer = window.setTimeout(settle, SNAP_SETTLE_DEBOUNCE_MS);
	};

	function settle() {
		clearTimeout(settleTimer);
		settleTimer = 0;
		if (held) {
			owed = true;
			suppressSnap();
			return;
		}
		owed = false;
		const was = scrolling;
		scrolling = false;
		handlers.onSettle(was);
	}

	const onScroll = () => {
		if (!scrolling) {
			scrolling = true;
			handlers.onStart?.();
		}
		if (!frame) frame = requestAnimationFrame(publish);
		// Re-armed per frame so it fires only once movement stops. `owed` puts it back in charge on a `scrollend`
		// engine, for a settle refused under a finger whose release produces no movement.
		if (!supportsScrollEnd || owed) armDebounce();
	};

	const onTouchStart = () => {
		held = true;
		owed = false;
		// A fresh gesture supersedes whatever the last one was still counting down to.
		clearTimeout(settleTimer);
		settleTimer = 0;
	};

	const onTouchEnd = (event: TouchEvent) => {
		if (event.touches.length > 0) return; // another finger is still down
		if (!held) return;
		held = false;
		// Before the debounce: the restyle starts the snap-back, whose scroll events keep re-arming it.
		restoreSnap();
		// Momentum and snap BEGIN at release, so only an owed settle (its `scrollend` spent under the finger) arms here.
		if (owed) armDebounce();
	};

	el.addEventListener('scroll', onScroll, { passive: true });
	el.addEventListener('touchstart', onTouchStart, { passive: true });
	window.addEventListener('touchend', onTouchEnd, { passive: true });
	window.addEventListener('touchcancel', onTouchEnd, { passive: true });
	if (supportsScrollEnd) el.addEventListener('scrollend', settle);

	return {
		stop: () => {
			if (frame) cancelAnimationFrame(frame);
			clearTimeout(settleTimer);
			restoreSnap();
			el.removeEventListener('scroll', onScroll);
			el.removeEventListener('touchstart', onTouchStart);
			window.removeEventListener('touchend', onTouchEnd);
			window.removeEventListener('touchcancel', onTouchEnd);
			if (supportsScrollEnd) el.removeEventListener('scrollend', settle);
		},
	};
}
